import { Link } from "react-router-dom";
import { MktHero, MktSection } from "../components/marketing";
import { openCookiePreferences } from "../components/cookie-consent";
import { Seo } from "../lib/seo";
import {
  BOT_MODEL_SENTENCE,
  COUNTRY,
  LEGAL_EMAIL as EMAIL,
  LegalContact,
  LegalMeta,
  OWNER,
  SubprocessorList,
} from "./legal-common";

/**
 * Public legal pages for ODIN (odinforge.io).
 *
 * Counsel-reviewed and IN FORCE — no longer drafts. Every hedge that deferred a
 * term to a future review ("subject to legal review", "before paid plans go
 * live") has been resolved, because paid plans are live and a customer must be
 * able to rely on what these pages say.
 *
 * The version note, the contact paragraph and the description of how a bot
 * reaches a customer's server come from ./legal-common, shared with /terms and
 * /privacy, so all nine documents say the same thing in the same words. Do not
 * re-declare them here.
 */

/* ------------------------------------------------------------------ Cookies */
export function CookiePolicy(): JSX.Element {
  return (
    <>
      <Seo
        title="Cookie Policy"
        description="How ODIN uses cookies and similar technologies, and how to control them."
        path="/legal/cookies"
      />
      <MktHero kicker="Legal" title="Cookie Policy" subhead="What we store in your browser, and how you stay in control." />
      <MktSection>
        <LegalMeta />
        <p className="mkt-section__lead">
          ODIN uses a small number of cookies and equivalent browser storage. We group them into
          categories and only use optional categories with your consent. You can change your choice at any
          time via{" "}
          <button type="button" className="mkt-linkbtn" onClick={openCookiePreferences}>
            Cookie preferences
          </button>
          .
        </p>
      </MktSection>
      <MktSection kicker="Categories" title="What each category does">
        <div className="mkt-prose">
          <ul>
            <li>
              <strong>Strictly necessary.</strong> Required to run the site and keep you securely signed
              in — session and CSRF protection, and storing your cookie choice. These cannot be switched
              off and do not require consent.
            </li>
            <li>
              <strong>Analytics (optional, off by default).</strong> Privacy-respecting measurement of how
              ODIN is used so we can improve it. Only set after you opt in.
            </li>
            <li>
              <strong>Marketing (optional, off by default).</strong> Not currently used. Reserved for any
              future marketing measurement, and only ever with your consent.
            </li>
          </ul>
          <p>
            A full, itemised list — name, purpose, duration and provider — is maintained in our cookie
            inventory and summarised here as it changes. Today ODIN loads no analytics or marketing
            scripts before consent. The site works fully with optional cookies rejected.
          </p>
        </div>
        <LegalContact />
      </MktSection>
    </>
  );
}

/* ------------------------------------------------------------- Acceptable use */
export function AcceptableUse(): JSX.Element {
  return (
    <>
      <Seo
        title="Acceptable Use Policy"
        description="The rules for using ODIN safely and lawfully."
        path="/legal/acceptable-use"
      />
      <MktHero kicker="Legal" title="Acceptable Use Policy" subhead="Using ODIN safely, lawfully and respectfully." />
      <MktSection>
        <LegalMeta />
        <p className="mkt-section__lead">
          You agree not to use ODIN, BALDER or the bot connected to your server to do any of the following,
          or to help anyone else do them.
        </p>
        <div className="mkt-prose">
          <ul>
            <li>Anything illegal, or that infringes others' rights.</li>
            <li>Harassment, abuse, threats, hateful conduct or targeting of individuals or groups.</li>
            <li>Distributing malware, phishing, credential theft, spam or fraud.</li>
            <li>Unauthorised access, privilege escalation, or accessing another tenant's data.</li>
            <li>
              Security testing of ODIN without our prior written permission (see the{" "}
              <Link to="/security">Security</Link> page).
            </li>
            <li>Circumventing Discord's policies, or violating any third-party service's terms.</li>
            <li>Unauthorised surveillance or privacy violations of community members.</li>
            <li>Excessive resource consumption, harmful automation or attempts to degrade the service.</li>
            <li>Platform manipulation, or misrepresenting ODIN, OKMedia or any third party.</li>
          </ul>
          <p>
            You are responsible for how your community and moderators use ODIN, and for the content you
            create with it. We may suspend access to protect the platform, our users or third parties, and
            will act proportionately.
          </p>
        </div>
        <LegalContact />
      </MktSection>
    </>
  );
}

/* -------------------------------------------------------------- Subprocessors */
export function Subprocessors(): JSX.Element {
  return (
    <>
      <Seo
        title="Subprocessors"
        description="The third-party providers ODIN relies on to deliver the service."
        path="/legal/subprocessors"
      />
      <MktHero kicker="Legal" title="Subprocessors" subhead="The independent providers that help us run ODIN." />
      <MktSection>
        <LegalMeta />
        <p className="mkt-section__lead">
          To operate ODIN, {OWNER} uses the independent service providers below. Each is governed by its own
          terms and processes data only as needed to provide its service. This list is maintained and
          updated as our infrastructure evolves; material changes are reflected here and in our privacy
          documentation.
        </p>
        <div className="mkt-prose">
          <SubprocessorList />
          <p>
            The same list, with the same descriptions, appears under <em>Sub-processors</em> on the{" "}
            <Link to="/privacy">Privacy Policy</Link>. What BALDER sends to our AI provider, and what is
            retained, is set out on the <Link to="/legal/ai-transparency">AI Transparency</Link> page.
          </p>
          <p>
            None of these providers owns, controls or endorses ODIN. Where processing involves personal
            data, we have data-processing agreements and the appropriate transfer safeguards in place with
            each of them.
          </p>
        </div>
        <LegalContact />
      </MktSection>
    </>
  );
}

/* ------------------------------------------------------------------------ DPA */
export function Dpa(): JSX.Element {
  return (
    <>
      <Seo
        title="Data Processing Agreement"
        description="How ODIN processes personal data on behalf of business customers."
        path="/legal/dpa"
      />
      <MktHero kicker="Legal" title="Data Processing Agreement" subhead="For business customers who need a DPA." />
      <MktSection>
        <LegalMeta />
        <p className="mkt-section__lead">
          Where {OWNER} processes personal data on your behalf as a processor (for example, community
          member data you manage through ODIN), this DPA describes our commitments. It forms part of the
          Terms for business customers and is available for review before signing.
        </p>
        <div className="mkt-prose">
          <ul>
            <li>
              <strong>Roles.</strong> You are the controller for the community data you manage; {OWNER} acts
              as processor and processes it only on your documented instructions.
            </li>
            <li>
              <strong>Confidentiality &amp; security.</strong> Personnel are bound by confidentiality; we
              apply technical and organisational security measures (documented in our security materials).
            </li>
            <li>
              <strong>Subprocessors.</strong> We use the providers listed on the{" "}
              <Link to="/legal/subprocessors">Subprocessors</Link> page and remain responsible for their
              performance.
            </li>
            <li>
              <strong>Data-subject assistance.</strong> We help you respond to access, deletion and
              correction requests.
            </li>
            <li>
              <strong>Incident notification.</strong> We notify you without undue delay after becoming aware
              of a personal-data breach affecting your data.
            </li>
            <li>
              <strong>Deletion &amp; return.</strong> On termination we delete or return personal data,
              subject to lawful retention (for example, accounting records).
            </li>
            <li>
              <strong>International transfers.</strong> Where data is transferred outside the EEA, we rely on
              the European Commission's Standard Contractual Clauses together with supplementary technical
              measures, including encryption in transit and at rest.
            </li>
          </ul>
          <p>
            A full, signable DPA including the technical-and-organisational-measures and processing
            appendices is available on request — email <a href={`mailto:${EMAIL}`}>{EMAIL}</a> and we will
            send you the current version to countersign.
          </p>
        </div>
        <LegalContact />
      </MktSection>
    </>
  );
}

/* -------------------------------------------------- Refunds and cancellations */
export function RefundsAndCancellations(): JSX.Element {
  return (
    <>
      <Seo
        title="Refunds & Cancellations"
        description="How cancellations, renewals and refunds work on ODIN."
        path="/legal/refunds-and-cancellations"
      />
      <MktHero kicker="Legal" title="Refunds & Cancellations" subhead="Clear, no-surprises billing." />
      <MktSection>
        <LegalMeta />
        <p className="mkt-section__lead">
          The Free plan costs €0 and carries no commitment. For paid plans, the terms below apply and are
          the terms we operate by; nothing here removes mandatory consumer rights that cannot lawfully be
          excluded.
        </p>
        <div className="mkt-prose">
          <ul>
            <li>
              <strong>Cancellation.</strong> You can cancel at any time. Your plan stays active until the
              end of the period you have paid for, then returns to Free. Nothing in your Discord server is
              deleted automatically on cancellation.
            </li>
            <li>
              <strong>Renewals.</strong> Paid plans renew automatically for the interval you chose (monthly
              or annual) until cancelled. We show the renewal amount and date before you subscribe.
            </li>
            <li>
              <strong>Failed payments.</strong> If a renewal payment fails, we apply a grace period and
              contact you before any suspension, rather than deleting your configuration.
            </li>
            <li>
              <strong>Consumer withdrawal rights.</strong> Where applicable law grants a right of
              withdrawal for digital services, that right applies as required by law.
            </li>
            <li>
              <strong>Refunds.</strong> We handle refund requests fairly and in line with applicable law and
              this policy. Signed-in customers can request a refund on the invoice itself,
              or open a <Link to="/app/support?topic=REFUND">refund support</Link> conversation.
            </li>
          </ul>
          {/* Part 10.2 — the in-app desk is presented FIRST, because for a signed-in
              customer it is the channel that records the conversation and gives them a
              reference. The address stays because this page is public: it is read by
              people who have no account, and by people who have lost access to one.
              See docs/support/SUPPORT_EMAIL_RETENTION.md. */}
          <p>
            Billing support: <Link to="/app/support?topic=BILLING">open a billing support conversation</Link>{" "}
            in your workspace. If you cannot sign in, email <a href={`mailto:${EMAIL}`}>{EMAIL}</a>.
          </p>
        </div>
        <LegalContact />
      </MktSection>
    </>
  );
}

/* --------------------------------------------------------------- AI transparency */
export function AiTransparency(): JSX.Element {
  return (
    <>
      <Seo
        title="AI Transparency"
        description="What BALDER is, what it does, and how your data is handled."
        path="/legal/ai-transparency"
      />
      <MktHero kicker="Legal" title="AI Transparency" subhead="How BALDER, ODIN's AI assistant, works with your data." />
      <MktSection>
        <LegalMeta />
        <p className="mkt-section__lead">
          BALDER is an AI assistant built into ODIN. It helps you understand your server, draft blueprint
          changes and produce dry-run previews in plain language. BALDER advises and explains — it never
          deploys or approves changes on its own, and you approve every change.
        </p>
        <div className="mkt-prose">
          <ul>
            <li>
              <strong>When AI is used.</strong> AI runs when you interact with BALDER or ask it to help with
              a task. It is not used to make automated decisions with legal or similarly significant effects
              about you.
            </li>
            <li>
              <strong>What is sent.</strong> To answer you, relevant context (such as your request and
              pertinent server-configuration details) is sent to our AI provider(s). We aim to send the
              minimum necessary and avoid sending secrets such as bot tokens.
            </li>
            <li>
              <strong>Retention &amp; training.</strong> Conversation records are retained to provide the
              feature and can be deleted. We do not sell your data. Provider-side training arrangements are
              documented in our privacy materials and configured to respect this intent.
            </li>
            <li>
              <strong>Accuracy limits.</strong> AI can be wrong or incomplete. Always review BALDER's
              suggestions and the dry-run preview before applying anything. Do not rely on BALDER for legal,
              financial or safety-critical decisions.
            </li>
            <li>
              <strong>Your controls.</strong> You choose when to use BALDER, and you can delete AI
              conversations. See the <Link to="/privacy">Privacy Policy</Link> for data rights.
            </li>
          </ul>
        </div>
        <LegalContact />
      </MktSection>
    </>
  );
}

/* -------------------------------------------------------------- Security page */
export function SecurityPage(): JSX.Element {
  return (
    <>
      <Seo
        title="Security"
        description="How ODIN is designed and operated securely, and how to report a vulnerability."
        path="/security"
      />
      <MktHero kicker="Trust" title="Security at ODIN" subhead="How we protect your account, your server and your data." />
      <MktSection>
        <p className="mkt-section__lead">
          Security is a core part of ODIN's design. We follow established practices and review the platform
          against defined controls. No system is ever perfectly secure, and we do not claim to be — but we
          work hard to keep ODIN hardened and to be transparent about how it works.
        </p>
        <div className="mkt-prose">
          <ul>
            <li>
              <strong>There is no bot token for you to hold.</strong> {BOT_MODEL_SENTENCE} If you do bring
              your own bot on Dedicated, that token is submitted once over an encrypted connection, stored
              encrypted, and never shown back to you — not even in the dashboard.
            </li>
            <li>
              <strong>Safety by default.</strong> Nothing structural reaches your Discord server until you
              review a dry run and approve it. Live deployment is locked by default.
            </li>
            <li>
              <strong>Tenant isolation.</strong> Each community's data is scoped and access is authorised
              server-side. Frontend visibility is never authorisation.
            </li>
            <li>
              <strong>Sessions &amp; secrets.</strong> Sessions use secure, HttpOnly cookies; secrets stay
              on the server and never enter the browser bundle.
            </li>
            <li>
              <strong>Hardened delivery.</strong> HTTPS everywhere, a strict Content-Security-Policy, HSTS
              and other security headers.
            </li>
          </ul>
          <h3>Reporting a vulnerability</h3>
          <p>
            We welcome good-faith security research. Please email{" "}
            <a href={`mailto:${EMAIL}`}>{EMAIL}</a> (also listed in{" "}
            <a href="/.well-known/security.txt">/.well-known/security.txt</a>). Do not access other tenants'
            data, run destructive tests, or degrade the service; report privately first and give us
            reasonable time to respond before any disclosure.
          </p>
          <p>
            ODIN is independently owned and operated by {OWNER} ({COUNTRY}). See our{" "}
            <Link to="/privacy">Privacy Policy</Link> and <Link to="/terms">Terms</Link> for more.
          </p>
        </div>
      </MktSection>
    </>
  );
}
